import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'

const AuthContext = createContext()

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/'

axios.defaults.baseURL = API_BASE_URL
axios.defaults.timeout = 10000

let requestInterceptorId = null
let responseInterceptorId = null

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('sgc_token'))
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false)

  useEffect(() => {
    if (token) {
      setupAuthInterceptor(token)
      validateToken()
    } else {
      setLoading(false)
    }

    return () => {
      if (requestInterceptorId !== null) {
        axios.interceptors.request.eject(requestInterceptorId)
        requestInterceptorId = null
      }
      if (responseInterceptorId !== null) {
        axios.interceptors.response.eject(responseInterceptorId)
        responseInterceptorId = null
      }
    }
  }, [token])

  const setupAuthInterceptor = (authToken) => {
    // Limpiar interceptores anteriores
    if (requestInterceptorId !== null) {
      axios.interceptors.request.eject(requestInterceptorId)
      requestInterceptorId = null
    }
    if (responseInterceptorId !== null) {
      axios.interceptors.response.eject(responseInterceptorId)
      responseInterceptorId = null
    }

    // Request interceptor
    requestInterceptorId = axios.interceptors.request.use(
      (config) => {
        if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && token) {
          // Solo hacer logout una vez para evitar loops
          console.warn('Token expirado, cerrando sesión')

          // Mostrar mensaje de sesión expirada
          const isLoginEndpoint = error.config?.url?.includes('/api/auth/login')
          if (!isLoginEndpoint) {
            // Solo mostrar mensaje si no es un intento de login fallido
            alert('Tu sesión ha expirado por inactividad. Por favor, inicia sesión nuevamente.')
          }

          await logoutSilent()
        }
        return Promise.reject(error)
      }
    )
  }

  const validateToken = async () => {
    try {
      const response = await axios.get('/api/auth/profile')
      setUser(response.data.user)
      // Verificar si requiere cambio de contraseña
      if (response.data.user?.requires_password_change) {
        setRequiresPasswordChange(true)
      }
    } catch (error) {
      console.error('Error validando token:', error)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      setLoading(true)
      const response = await axios.post('/api/auth/login', credentials)
      const { access_token: newToken, user: userData } = response.data

      localStorage.setItem('sgc_token', newToken)
      setToken(newToken)
      setUser(userData)
      setupAuthInterceptor(newToken)

      // Verificar si requiere cambio de contraseña
      if (userData?.requires_password_change) {
        setRequiresPasswordChange(true)
      }

      return { success: true, user: userData }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || 'Error de conexión'
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  const logoutSilent = async () => {
    // Logout sin hacer llamada al servidor (para tokens expirados)
    localStorage.removeItem('sgc_token')
    setToken(null)
    setUser(null)
    setRequiresPasswordChange(false)

    if (requestInterceptorId !== null) {
      axios.interceptors.request.eject(requestInterceptorId)
      requestInterceptorId = null
    }
    if (responseInterceptorId !== null) {
      axios.interceptors.response.eject(responseInterceptorId)
      responseInterceptorId = null
    }
  }

  const logout = async () => {
    try {
      if (token) {
        // Intentar llamar al endpoint de logout, pero no fallar si hay error
        await axios.post('/api/auth/logout').catch(() => {
          // Ignorar errores del logout
        })
      }
    } catch (error) {
      console.error('Error en logout:', error)
    } finally {
      await logoutSilent()
    }
  }

  const updateProfile = async (profileData) => {
    try {
      const currentToken = token || localStorage.getItem('sgc_token')
      const response = await axios.put('/api/auth/profile', profileData, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      })
      setUser(response.data.user)
      return { success: true, user: response.data.user }
    } catch (error) {
      console.error('Error actualizando perfil:', error)
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || 'Error actualizando perfil'
      return { success: false, error: errorMessage }
    }
  }

  const hasPermission = (requiredRoles) => {
    if (!user) return false
    
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles]
    return roles.includes(user.rol)
  }

  const canAccessGestion = (gestionId) => {
    if (!user) return false
    if (user.rol === 'administrador') return true
    if (user.rol === 'consultor') return true
    if (user.rol === 'editor') {
      // Verificar si el editor tiene acceso a esta gestión
      return user.gestiones && user.gestiones.some(g => g.id === gestionId)
    }
    return false
  }

  const canEditGestion = (gestionId) => {
    if (!user) return false
    if (user.rol === 'administrador') return true
    if (user.rol === 'editor' || user.rol === 'consultor') {
      // Verificar si el usuario puede editar esta gestión específica
      const gestion = user.gestiones?.find(g => g.id === gestionId)
      return gestion && gestion.puede_editar
    }
    return false
  }

  const handlePasswordChanged = async () => {
    // Actualizar el estado del usuario después de cambiar la contraseña
    setRequiresPasswordChange(false)
    // Actualizar los datos del usuario desde el servidor
    try {
      const response = await axios.get('/api/auth/profile')
      setUser(response.data.user)
    } catch (error) {
      console.error('Error actualizando perfil:', error)
    }
  }

  const value = {
    user,
    loading,
    login,
    logout,
    updateProfile,
    hasPermission,
    canAccessGestion,
    canEditGestion,
    requiresPasswordChange,
    handlePasswordChanged,
    isAuthenticated: !!user,
    isAdmin: user?.rol === 'administrador',
    isEditor: user?.rol === 'editor',
    isConsultor: user?.rol === 'consultor'
  }

  // Log temporal para debugging
  if (user) {
    console.log('AuthContext VALUE - user.rol:', user.rol, 'isAdmin:', value.isAdmin, 'user object:', user)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider')
  }
  return context
}